// The hosted-tournament leaderboard. Pure.
//
// THIS IS THE SHARED SOURCE OF TRUTH. The phone, the clubhouse screen and the host's
// console all render from this one function, which is why `public_leaderboard()` in SQL
// returns raw scores rather than computed standings — two implementations of golf's
// ranking rules would eventually disagree about who is winning, in front of a field.
//
// Two things here are easy to get wrong and are the reason it is not a one-liner.
//
// RANKING IS BY TO-PAR, NOT BY TOTAL. Mid-round, groups have played different numbers of
// holes. A team three under through 3 is ahead of a team level through 9, and comparing
// raw totals would put them the other way round. To-par is the only figure comparable
// across different `thru` counts, which is exactly why every live golf leaderboard shows
// it and why this sorts on it.
//
// THRU IS A COUNT, NOT A POSITION. With a shotgun start a group beginning on hole 5 has
// played holes 5, 6, 7 — "thru 3", not "thru 7". Counting completed holes rather than
// reading the highest hole number is what makes the board correct at an outing.

import { allocateStrokes } from '../engine/handicap';
import { teamCourseHandicap } from '../engine/games/scramble';
import { scoringTeamBoard, scoringTeamCashBoard } from '../engine/tieredTeams';
import type { ScoringTeamRow, TeamBasis, TeamCashRow, TeamMode, TeamRule } from '../engine/tieredTeams';
import { fieldSkins } from '../engine/fieldSkins';
import type { FieldSkinsBasis, FieldSkinsPlayer, FieldSkinsResult } from '../engine/fieldSkins';

export type { ScoringTeamRow, TeamCashRow, TeamMode } from '../engine/tieredTeams';
export type { FieldSkinsResult } from '../engine/fieldSkins';

export type LeaderboardEntrant = {
  id: string;
  displayName: string | null;
  /** Only read when scoring is 'net'. */
  courseHandicap?: number | null;
  /** Left the event after starting. See the ranking note on `placeRows`. */
  withdrawn?: boolean;
};

export type LeaderboardTeam = {
  id: string;
  name: string;
  /** "7A" — shown so a host can find a group on the course. */
  slotLabel?: string | null;
  entrants: LeaderboardEntrant[];
  /** One row per entrant per completed hole. Absent = not played. */
  scores: Array<{ entrantId: string; holeIdx: number; gross: number }>;
};

export type LeaderboardInput = {
  format: 'scramble' | 'individual';
  scoring: 'gross' | 'net';
  /** Par per played hole, index-aligned with hole indexes in `scores`. */
  holePars: number[];
  /** Needed only for net. */
  holeStrokeIndexes?: number[];
  /** Percentage blend for a scramble team's handicap; the engine default is used if absent. */
  handicapPercents?: number[];
  teams: LeaderboardTeam[];
};

export type LeaderboardRow = {
  /** Team id in a scramble, entrant id in an individual event. */
  id: string;
  name: string;
  /** The players on a team, or the team a player is in. */
  subtitle: string;
  slotLabel?: string | null;
  /** Holes COMPLETED. Never the highest hole number reached — see the note above. */
  thru: number;
  gross: number;
  /** Null under gross scoring. */
  net: number | null;
  /** Against the par of the holes actually played. The figure the board is sorted on. */
  toPar: number;
  /** "1", "T2", "T2", "4". Empty for anyone yet to hole out anywhere. */
  position: string;
  /** 0 for anyone unranked — not started, or withdrawn — so they sort last without
   *  claiming a place. */
  rank: number;
  started: boolean;
  /** Individual play only: this player walked in. Shown, but never placed. */
  withdrawn: boolean;
};

/** Golf's to-par convention: level is E, under is bare, over carries a plus. */
export const fmtToPar = (n: number): string => (n === 0 ? 'E' : n > 0 ? `+${n}` : `${n}`);

/**
 * The one score a scramble team made on a hole.
 *
 * Every member carries it, so any of them answers. Teammates holding different numbers is a
 * miskeyed card; the lowest is taken, matching what `scrambleEngine` does, so the board and
 * the card can never disagree about the same hole.
 */
function teamScoreOnHole(
  byHole: Map<number, number[]>,
  holeIdx: number
): number | null {
  const vals = byHole.get(holeIdx);
  if (!vals || vals.length === 0) return null;
  return Math.min(...vals);
}

type Tally = { thru: number; gross: number; net: number; parPlayed: number };

function tally(
  holesPlayed: number[],
  scoreAt: (holeIdx: number) => number | null,
  strokesAt: (holeIdx: number) => number,
  holePars: number[]
): Tally {
  let thru = 0;
  let gross = 0;
  let net = 0;
  let parPlayed = 0;
  for (const holeIdx of holesPlayed) {
    const s = scoreAt(holeIdx);
    if (s === null) continue;
    thru += 1;
    gross += s;
    net += s - strokesAt(holeIdx);
    parPlayed += holePars[holeIdx] ?? 4;
  }
  return { thru, gross, net, parPlayed };
}

/**
 * Rank rows by to-par, sharing a position on a tie.
 *
 * Two groups are deliberately left UNRANKED rather than placed.
 *
 * Anyone with no holes yet, because at a shotgun start every group is on zero for the first
 * fifteen minutes and a board declaring thirty teams tied for first is useless.
 *
 * And anyone WITHDRAWN, because golf does not place a player who did not finish. Someone who
 * walked in at the turn two under would otherwise sit in the top three all afternoon on nine
 * holes, ahead of people who played eighteen. They stay on the board, shown and marked, but
 * they do not hold a position. Only individual play can produce this: in a scramble the row
 * is the TEAM, and a team plays on when one of its members leaves.
 */
function placeRows(rows: LeaderboardRow[]): LeaderboardRow[] {
  const rankable = (r: LeaderboardRow) => r.started && !r.withdrawn;
  const started = rows.filter(rankable).sort((a, b) => a.toPar - b.toPar || b.thru - a.thru);
  const notStarted = rows
    .filter((r) => !rankable(r))
    .sort((a, b) => Number(a.withdrawn) - Number(b.withdrawn) || a.name.localeCompare(b.name));

  let i = 0;
  while (i < started.length) {
    let j = i;
    while (j + 1 < started.length && started[j + 1].toPar === started[i].toPar) j++;
    const rank = i + 1;
    const tied = j > i;
    for (let k = i; k <= j; k++) {
      started[k].rank = rank;
      started[k].position = tied ? `T${rank}` : `${rank}`;
    }
    i = j + 1;
  }

  for (const row of notStarted) {
    row.rank = 0;
    row.position = '';
  }

  return [...started, ...notStarted];
}

export function leaderboard(input: LeaderboardInput): LeaderboardRow[] {
  const { format, scoring, holePars, teams } = input;
  const net = scoring === 'net';
  const si = input.holeStrokeIndexes;

  const rows: LeaderboardRow[] = [];

  for (const team of teams) {
    // hole → the scores recorded on it, by whichever entrants have one.
    const teamByHole = new Map<number, number[]>();
    // entrant → hole → score, for individual play.
    const perEntrant = new Map<string, Map<number, number>>();

    for (const s of team.scores) {
      const list = teamByHole.get(s.holeIdx) ?? [];
      list.push(s.gross);
      teamByHole.set(s.holeIdx, list);

      const own = perEntrant.get(s.entrantId) ?? new Map<number, number>();
      own.set(s.holeIdx, s.gross);
      perEntrant.set(s.entrantId, own);
    }

    const nameOf = (e: LeaderboardEntrant) => e.displayName?.trim() || 'TBD';

    if (format === 'scramble') {
      const holes = [...teamByHole.keys()].sort((a, b) => a - b);

      let strokes: number[] = [];
      let teamCh = 0;
      if (net && si) {
        teamCh = teamCourseHandicap(
          team.entrants.map((e) => e.courseHandicap ?? 0),
          input.handicapPercents
        );
        strokes = allocateStrokes(teamCh, si);
      }

      const t = tally(
        holes,
        (h) => teamScoreOnHole(teamByHole, h),
        (h) => strokes[h] ?? 0,
        holePars
      );

      rows.push({
        id: team.id,
        name: team.name,
        subtitle: team.entrants.map(nameOf).join(', '),
        slotLabel: team.slotLabel ?? null,
        thru: t.thru,
        gross: t.gross,
        net: net ? t.net : null,
        toPar: (net ? t.net : t.gross) - t.parPlayed,
        position: '',
        rank: 0,
        started: t.thru > 0,
        // A team carries on when a member walks in, so a team row is never withdrawn.
        withdrawn: false,
      });
      continue;
    }

    // Individual play: every entrant is their own line, and the team is only the group
    // they happened to walk round with.
    for (const entrant of team.entrants) {
      const own = perEntrant.get(entrant.id) ?? new Map<number, number>();
      const holes = [...own.keys()].sort((a, b) => a - b);

      const strokes =
        net && si ? allocateStrokes(entrant.courseHandicap ?? 0, si) : ([] as number[]);

      const t = tally(holes, (h) => own.get(h) ?? null, (h) => strokes[h] ?? 0, holePars);

      rows.push({
        id: entrant.id,
        name: nameOf(entrant),
        subtitle: team.name,
        slotLabel: team.slotLabel ?? null,
        thru: t.thru,
        gross: t.gross,
        net: net ? t.net : null,
        toPar: (net ? t.net : t.gross) - t.parPlayed,
        position: '',
        rank: 0,
        started: t.thru > 0,
        withdrawn: entrant.withdrawn === true,
      });
    }
  }

  return placeRows(rows);
}

/**
 * How far along the whole field is — the host's "are we nearly done" figure.
 *
 * `started` means STILL OUT THERE, so it excludes anyone who withdrew. They started, but a
 * host reading "4 on the course" wants to know how many groups they are still waiting on
 * before they can hand out prizes, and somebody who walked in at the turn is not one of
 * them. Counted separately instead, since a host does want to know it happened.
 */
export function fieldProgress(rows: LeaderboardRow[], holeCount: number): {
  started: number;
  finished: number;
  withdrawn: number;
  total: number;
} {
  return {
    started: rows.filter((r) => r.started && !r.withdrawn).length,
    finished: rows.filter((r) => r.thru >= holeCount && !r.withdrawn).length,
    withdrawn: rows.filter((r) => r.withdrawn).length,
    total: rows.length,
  };
}

// ── Tiered scoring teams off the same public_leaderboard JSON ──────────────────
// `public_leaderboard()` (supabase/migrations/0021) carries a scoring team's members
// scattered across `teams[].entrants` — a scoring team is not a walking group, its members
// can be on different cards — plus a top-level `scoring_teams` array naming the teams
// themselves. This is the one place that JSON is turned into `scoringTeamBoard()` rows, so
// the web console's LeaderboardPage and this phone's HostedLeaderboardScreen read the same
// standings off the same wire payload, the identical reasoning `toLeaderboardInput` (in
// src/sync/hosted.ts) already applies one level up for the group board.
//
// Kept independent of hosted.ts's own RPC types (duplicating the handful of fields this
// needs) rather than importing them, so this stays a leaf module hosted.ts can import from —
// hosted.ts already imports `leaderboard` and `LeaderboardInput` from here.

type ScoringPayloadEntrant = {
  id: string;
  name?: string | null;
  course_handicap?: number | null;
  withdrawn?: boolean | null;
  scoring_team_id?: string | null;
  tier?: number | null;
};
type ScoringPayloadScore = { entrant_id: string; hole_idx: number; gross: number };
type ScoringPayloadTeam = {
  /** Only read under `team_mode: 'same'`, where a walking group's own id/name become the
   *  scoring team's — see `scoringTeamRowsFromPayload`. */
  id?: string;
  name?: string | null;
  /** The walking group's own signup captain (supabase/migrations/0001) — only read under
   *  `team_mode: 'same'`, where the foursome IS the scoring team, so its captain becomes the
   *  team's captain too. See supabase/migrations/0023_team_captains.sql. */
  captain_name?: string | null;
  entrants?: ScoringPayloadEntrant[] | null;
  scores?: ScoringPayloadScore[] | null;
};
type ScoringPayloadTournament = {
  team_rule?: TeamRule | null;
  team_basis?: TeamBasis | null;
  hole_pars?: number[] | null;
  hole_indexes?: (number | null)[] | null;
  /** separate (default, including absent — an older RPC never sent this): scoring teams are
   *  their own drafted pool. same: the walking groups ARE the scoring teams. See
   *  supabase/migrations/0022_team_mode.sql. */
  team_mode?: TeamMode | null;
  /** Only read by `scoringTeamCashRowsFromPayload` — a charity outing that leaves this off
   *  gets no cash column, same reasoning 0011 already applies to the per-player one. */
  show_money?: boolean | null;
  /** off (default, and what an older RPC with no such column always meant): no field-wide
   *  skins pool. gross/net: the whole field plays one pool across every card — see
   *  `fieldSkinsFromPayload` and supabase/migrations/0024_field_skins.sql. */
  field_skins?: 'off' | 'gross' | 'net' | null;
  /** Cents PER PLAYER. Only meaningful when `field_skins` is not 'off'. Not read when
   *  `field_skins_per_skin` is set — see that field. */
  field_skins_buy_in?: number | null;
  /** true (default): a tied hole rolls into the next outright win. false: void — see
   *  src/engine/fieldSkins.ts. */
  field_skins_carry?: boolean | null;
  /** player (default, and what an older RPC with no such column always meant): the field
   *  skins pool above is every entrant. team: one pseudo-player per team instead — see
   *  `fieldSkinsFromPayload` and supabase/migrations/0025_field_skins_team.sql. */
  field_skins_unit?: 'player' | 'team' | null;
  /** Cents. 0 (default): field_skins_buy_in's pot splits proportionally by skins won.
   *  Greater than 0: every skin pays this fixed amount instead — see
   *  src/engine/fieldSkins.ts's `perSkinCents`. */
  field_skins_per_skin?: number | null;
};

export type ScoringTeamsPayload = {
  tournament: ScoringPayloadTournament;
  teams?: ScoringPayloadTeam[] | null;
  /** `captain_name` (supabase/migrations/0023_team_captains.sql) is the drafted-member
   *  captain's display name, or null if the host has not set one — never the email or phone,
   *  which public_leaderboard() never sends. */
  scoring_teams?: Array<{ id: string; name: string; seq: number; captain_name?: string | null }> | null;
};

/** Same shape `usableStrokeIndexes` in hosted.ts validates — a clean 1..n permutation, or
 *  net silently falls back to gross rather than allocate strokes off garbage. */
function validStrokeIndexes(raw: (number | null)[] | null | undefined, holes: number): number[] | null {
  if (!Array.isArray(raw) || holes === 0 || raw.length !== holes) return null;
  const seen = new Set<number>();
  for (const v of raw) {
    if (typeof v !== 'number' || !Number.isInteger(v) || v < 1 || v > holes) return null;
    if (seen.has(v)) return null;
    seen.add(v);
  }
  return raw as number[];
}

/** `public_leaderboard()` JSON → the shared `scoringTeamBoard()` engine's rows. Exported and
 *  tested on its own — it is JSON off the wire, so every optional field is treated as
 *  possibly absent, same discipline `toLeaderboardInput` follows for the group board.
 *
 *  MODE DECIDES WHAT "TEAM" MEANS HERE. Under `team_mode: 'same'` (default 'separate' for an
 *  older RPC or an absent field — see 0022) there is no drafted pool to read: the walking
 *  groups in `payload.teams` ARE the scoring teams, so this treats each group as its own
 *  one-team roster — id/name off the group itself, seq its index in payload order, and every
 *  one of its entrants scored as a member of that same group. `payload.scoring_teams` is
 *  simply not read in this mode (it is expected to be empty — nothing calls
 *  `apply_scoring_teams` in a same-mode event).
 *
 * Shared with `fieldSkinsFromPayload`'s team-unit pool below — a team skins pool reuses
 * exactly these teams and members, just fed to `scoringTeamBoard` with a forced `best1` rule
 * instead of the tournament's own `team_rule`. */
function scoringTeamUnitsFromPayload(payload: ScoringTeamsPayload): {
  teams: Parameters<typeof scoringTeamBoard>[0]['teams'];
  entrants: Parameters<typeof scoringTeamBoard>[0]['entrants'];
} {
  const t = payload.tournament;
  const sameMode = t.team_mode === 'same';

  const payloadTeams = payload.teams ?? [];
  const teams = sameMode
    ? payloadTeams.map((tm, i) => ({
        id: tm.id ?? `group-${i}`,
        name: tm.name?.trim() || `Group ${i + 1}`,
        seq: i,
        // Same mode: the foursome's own signup captain (0001) IS the scoring team's captain —
        // decided 2026-09-15, no separate captain concept for this mode.
        captainName: tm.captain_name ?? null,
      }))
    : (payload.scoring_teams ?? []).map((st) => ({
        id: st.id,
        name: st.name,
        seq: st.seq,
        captainName: st.captain_name ?? null,
      }));

  const entrants: Parameters<typeof scoringTeamBoard>[0]['entrants'] = [];
  for (let i = 0; i < payloadTeams.length; i++) {
    const team = payloadTeams[i];
    const scoresByEntrant = new Map<string, Array<{ holeIdx: number; gross: number }>>();
    for (const s of team.scores ?? []) {
      const list = scoresByEntrant.get(s.entrant_id) ?? [];
      list.push({ holeIdx: Number(s.hole_idx), gross: Number(s.gross) });
      scoresByEntrant.set(s.entrant_id, list);
    }
    const groupId = team.id ?? `group-${i}`;
    for (const e of team.entrants ?? []) {
      entrants.push({
        id: e.id,
        name: e.name?.trim() || 'TBD',
        scoringTeamId: sameMode ? groupId : e.scoring_team_id ?? null,
        tier: typeof e.tier === 'number' ? e.tier : null,
        courseHandicap: typeof e.course_handicap === 'number' ? e.course_handicap : null,
        withdrawn: e.withdrawn === true,
        scores: scoresByEntrant.get(e.id) ?? [],
      });
    }
  }

  return { teams, entrants };
}

export function scoringTeamRowsFromPayload(payload: ScoringTeamsPayload): ScoringTeamRow[] {
  const t = payload.tournament;
  const rule: TeamRule = t.team_rule === 'best1' || t.team_rule === 'aggregate' ? t.team_rule : 'best2';
  const basis: TeamBasis = t.team_basis === 'net' ? 'net' : 'gross';
  const holePars = (t.hole_pars ?? []).map(Number);
  const holeStrokeIndexes = validStrokeIndexes(t.hole_indexes, holePars.length);
  const { teams, entrants } = scoringTeamUnitsFromPayload(payload);

  return scoringTeamBoard({ rule, basis, holePars, holeStrokeIndexes, teams, entrants });
}

// ── Team cash board (SEPARATE mode only) ────────────────────────────────────────
// `scoringTeamCashBoard()` (src/engine/tieredTeams.ts) needs each entrant's own net side-game
// money, and — unlike everything above — this file does not compute that itself.
//
// WHY THE CALLER SUPPLIES IT. Side-game money is settled PER FOURSOME by replaying that
// group's own registered games through the engines (buildCtx + ENGINES + netMoney — see
// src/sync/toDomain.ts and, for the reference implementation, web/src/pages/LeaderboardPage.tsx's
// `boardMoney`/`computeGroupMoney`). That machinery already exists once per platform — the web
// console's LeaderboardPage and this app's HostedLeaderboardScreen — and pulling it into this
// shared leaf module would mean every caller of `leaderboard()` (which does not need money)
// pays for importing all nineteen engines. So the platform screen computes its own
// `Record<entrantId, cents>` the way it already does (or, on the phone, the way
// HostedLeaderboardScreen.tsx now does) and hands it in here.
//
// `moneyByEntrantFromRows` — a helper that would derive that record FROM `LeaderboardRow[]` —
// was deliberately not added. Checked the shape: `LeaderboardRow` (this file's own group-board
// rows) never carries money on itself. Every existing caller keeps it as a side lookup instead
// (web/src/pages/LeaderboardPage.tsx passes `money={money?.byEntrant ?? null}` to `Standings`
// alongside — not on — `board.rows`; see that file's own comment on why: "a separate lookup
// rather than a field on LeaderboardRow, because it is a different figure from a different
// scope"). There is nothing on a `LeaderboardRow` to derive money from.
//
// THE SCRAMBLE CASE. In a scramble, `LeaderboardRow.id` is a TEAM id, not a person's — but
// that row is the WALKING team, never a scoring team, and the two need not coincide (a scoring
// team's members can be spread across several different scrambles). `moneyByEntrantId` here is
// still keyed by raw ENTRANT id regardless of format — each scramble team's own side games
// still settle among its four members individually, the same as an individual event's group —
// so no scramble-specific handling is needed in this function; a caller only has to make sure
// the record it hands in is keyed by entrant id, not by the scramble team id `leaderboard()`
// itself would show.

/** `public_leaderboard()` JSON + a caller-computed per-entrant money lookup → the SEPARATE-mode
 *  team cash board. Returns `[]` in SAME mode (a foursome's own games net to zero inside
 *  itself by construction — see this file's header comment above and 0022's own note) and
 *  when the tournament does not show money at all, matching `ScoringTeamsSection`'s own
 *  "an empty section is worse than no section" convention. */
export function scoringTeamCashRowsFromPayload(
  payload: ScoringTeamsPayload,
  moneyByEntrantId: Record<string, number>
): TeamCashRow[] {
  const t = payload.tournament;
  if (t.team_mode === 'same') return [];
  if (t.show_money !== true) return [];

  const teams = (payload.scoring_teams ?? []).map((st) => ({ id: st.id, name: st.name, seq: st.seq }));

  const entrants: Parameters<typeof scoringTeamCashBoard>[0]['entrants'] = [];
  for (const team of payload.teams ?? []) {
    for (const e of team.entrants ?? []) {
      entrants.push({
        id: e.id,
        name: e.name?.trim() || 'TBD',
        scoringTeamId: e.scoring_team_id ?? null,
        tier: typeof e.tier === 'number' ? e.tier : null,
        moneyCents: moneyByEntrantId[e.id] ?? 0,
        withdrawn: e.withdrawn === true,
      });
    }
  }

  return scoringTeamCashBoard({ teams, entrants });
}

// ── Field-wide skins pool ────────────────────────────────────────────────────
// `fieldSkins()` (src/engine/fieldSkins.ts) needs the WHOLE FIELD as one flat player list,
// unlike everything else in this file that groups by walking team or scoring team — a field
// skins pool's low score is compared across every card at once, so there is only one "team":
// the tournament itself. See supabase/migrations/0024_field_skins.sql for the switch.

/** `public_leaderboard()` JSON → the shared `fieldSkins()` engine's input, or null when the
 *  pool is off (or an older RPC never sent the column at all — the same "absent means off"
 *  convention `team_mode`/`show_money` already use). Exported and tested on its own, same
 *  discipline as `scoringTeamRowsFromPayload` above.
 *
 * WITHDRAWN PLAYERS ARE LEFT OUT ENTIRELY in PLAYER unit, not merely unranked the way
 * `leaderboard()`'s own `placeRows` treats them. A field skins pool needs EVERY remaining
 * player's score on a hole before that hole can be decided (see fieldSkins.ts's own PENDING
 * note) — keeping a walked-in player in the field would freeze the whole pool at the hole they
 * left on, forever pending, for every other player still finishing the round. They are assumed
 * to have settled out of the pool by hand, the same way this app never tracks who actually
 * paid a buy-in at all. TEAM unit does not add this filter of its own — `scoringTeamBoard`'s
 * `best1` rule already skips a member with no score on a hole (see below), which is a
 * withdrawn player's exact situation from the hole they left on.
 *
 * TEAM UNIT reuses `scoringTeamUnitsFromPayload` — the SAME teams and members the Teams board
 * (`scoringTeamRowsFromPayload`) reads — through `scoringTeamBoard` with a forced `best1` rule
 * (a skins game always plays best-ball, regardless of the tournament's own `team_rule`) and
 * `basis` set to field skins' own gross/net switch, not `team_basis`. `best1`'s per-hole
 * `holes[]` is already exactly what this needs: the team's low member score that hole (net
 * already applied), or null when nobody on the team has one yet — the same null the engine
 * below reads as PENDING. Because strokes are already baked into those numbers, the pseudo
 * players are handed to `fieldSkins()` with `basis: 'gross'` and no stroke indexes of their
 * own — netting them again would be double-counting.
 */
export function fieldSkinsFromPayload(payload: ScoringTeamsPayload): FieldSkinsResult | null {
  const t = payload.tournament;
  if (t.field_skins !== 'gross' && t.field_skins !== 'net') return null;

  const holePars = (t.hole_pars ?? []).map(Number);
  const holeCount = holePars.length;
  if (holeCount === 0) return null;

  // Same fallback rule `scoringTeamRowsFromPayload` and `toLeaderboardInput` both already
  // apply: net without usable stroke indexes is an honest gross pool, never a confidently
  // wrong net one.
  const strokeIndexes = validStrokeIndexes(t.hole_indexes, holeCount);
  const basis: FieldSkinsBasis = t.field_skins === 'net' && strokeIndexes ? 'net' : 'gross';

  const carry = t.field_skins_carry !== false;
  const buyInCents = typeof t.field_skins_buy_in === 'number' ? t.field_skins_buy_in : 0;
  const perSkinCents = typeof t.field_skins_per_skin === 'number' ? t.field_skins_per_skin : 0;

  let players: FieldSkinsPlayer[];
  let engineBasis: FieldSkinsBasis;
  let engineStrokeIndexes: number[];

  if (t.field_skins_unit === 'team') {
    const { teams, entrants } = scoringTeamUnitsFromPayload(payload);
    if (teams.length === 0) return null;
    const rows = scoringTeamBoard({
      rule: 'best1',
      basis,
      holePars,
      holeStrokeIndexes: strokeIndexes,
      teams,
      entrants,
    });
    players = rows.map((r) => ({ id: r.id, name: r.name, courseHandicap: null, scores: r.holes }));
    engineBasis = 'gross';
    engineStrokeIndexes = [];
  } else {
    const list: FieldSkinsPlayer[] = [];
    for (const team of payload.teams ?? []) {
      const scoreAt = new Map<string, Map<number, number>>();
      for (const s of team.scores ?? []) {
        const holeMap = scoreAt.get(s.entrant_id) ?? new Map<number, number>();
        holeMap.set(Number(s.hole_idx), Number(s.gross));
        scoreAt.set(s.entrant_id, holeMap);
      }
      for (const e of team.entrants ?? []) {
        if (e.withdrawn === true) continue;
        const own = scoreAt.get(e.id);
        const scores: Array<number | null> = [];
        for (let h = 0; h < holeCount; h++) scores.push(own?.get(h) ?? null);
        list.push({
          id: e.id,
          name: e.name?.trim() || 'TBD',
          courseHandicap: typeof e.course_handicap === 'number' ? e.course_handicap : null,
          scores,
        });
      }
    }
    players = list;
    engineBasis = basis;
    engineStrokeIndexes = strokeIndexes ?? [];
  }

  // Nobody left in the pool (an all-withdrawn field, a team pool with no teams drafted yet, or
  // a tournament with no entrants at all) — nothing for the engine to sweep.
  if (players.length === 0) return null;

  return fieldSkins({
    basis: engineBasis,
    carry,
    buyInCents,
    perSkinCents,
    holeCount,
    players,
    strokeIndexes: engineStrokeIndexes,
  });
}
